import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ComplaintModalProvider } from './context/ComplaintModalContext';
import ErrorBoundary from './components/ErrorBoundary';
import TitleManager from './components/TitleManager';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';
import HomePage from './pages/HomePage';
import ComplaintPage from './pages/ComplaintPage';
import ReclamacaoPage from './pages/ReclamacaoPage';
import ResponderPage from './pages/ResponderPage';
import CandidatoPublicoPage from './pages/CandidatoPublicoPage';
import FeedbackCandidaturaPage from "./pages/FeedbackCandidaturaPage";
import AutorizarPage from './pages/AutorizarPage';
import CompanyPage from './pages/CompanyPage';
import LoginPage from './pages/LoginPage';
import IndicadoresPage from './pages/IndicadoresPage';
import CurriculoPage from './pages/CurriculoPage';
import VagasPage from './pages/VagasPage';
import NovaVagaPage from './pages/NovaVagaPage';
import VagaPage from './pages/VagaPage';
import OportunidadesPage from './pages/OportunidadesPage';
import CompaniesPage from './pages/CompaniesPage';
import HowItWorksPage from './pages/HowItWorksPage';
import ParaEmpresasPage from './pages/ParaEmpresasPage';
import MotivoPage from './pages/MotivoPage';
import PainelPage from './pages/PainelPage';
import ContaPage from './pages/ContaPage';
import MinhasReclamacoesPage from './pages/MinhasReclamacoesPage';
import PorQuePage from './pages/PorQuePage';
import PerguntasPage from './pages/PerguntasPage';
import GuiaRhPage from './pages/GuiaRhPage';
import CobrarRetornoPage from './pages/CobrarRetornoPage';
import EsqueciSenhaPage from './pages/EsqueciSenhaPage';
import RedefinirSenhaPage from './pages/RedefinirSenhaPage';
import ConfirmarEmailPage from './pages/ConfirmarEmailPage';
import TermosPage from './pages/TermosPage';
import PrivacidadePage from './pages/PrivacidadePage';
import SeloPage from './pages/SeloPage';
import GuiaRetornoPage from './pages/GuiaRetornoPage';
import NotFoundPage from './pages/NotFoundPage';
import ParceirosDemoPage from './pages/ParceirosDemoPage';
import ParceiroPainelPage from "./pages/ParceiroPainelPage";
import CapacitacaoPage from "./pages/CapacitacaoPage";

// Tira pontuacao grudada no fim da URL e reencaminha. Sem sujeira, e 404 de verdade.
function UrlSuja() {
  const { pathname, search, hash } = useLocation();
  const limpo = pathname.replace(/[.,;:!?)\]}'"”’]+$/, '');
  if (limpo && limpo !== pathname) return <Navigate to={limpo + search + hash} replace />;
  return <NotFoundPage />;
}

// Rodapé e robô de ajuda em todas as páginas — MENOS na janela própria da sala de aula (?janela=1), que é tela
// cheia de reunião e não deve ter nada do site em volta (Nelson, 17/10/2026).
function MolduraDoSite() {
  const { pathname, search } = useLocation();
  const q = new URLSearchParams(search);
  if ((pathname === '/parceiros/demo' || pathname === '/aula') && (q.get('janela') === '1' || q.has('convite'))) return null;   // link pessoal da aula também é tela limpa
  return (<><Footer /><ChatWidget /></>);
}

// Lazy: o painel admin (~1.150 linhas) só baixa quando alguém abre /admin, tirando peso do bundle inicial dos visitantes.
const AdminPage = lazy(() => import('./pages/AdminPage'));

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
      <BrowserRouter>
        <TitleManager />
        <ComplaintModalProvider>
        <div className="flex min-h-screen flex-col">
          <div className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              {/* 📝 /relatar é o endereço NOVO (16/09/2026): a página inteira já dizia "relato" — título, aba,
                  links do servidor — e só a URL continuava dizendo "denunciar", que é a palavra que aparece nos
                  posts e nos e-mails. "Denúncia" filtra o público para quem está com raiva e afasta os dois lados
                  que a gente quer: quem teve processo BOM (há elogios no portal) e a empresa que responderia.
                  /denunciar continua valendo PARA SEMPRE: está em post publicado, em e-mail enviado e no Google. */}
              <Route path="/relatar" element={<ComplaintPage />} />
              <Route path="/denunciar" element={<ComplaintPage />} />
              <Route path="/relato/:id" element={<ReclamacaoPage />} />
              <Route path="/responder/:token" element={<ResponderPage />} />
              <Route path="/candidato/:token" element={<CandidatoPublicoPage />} />
              <Route path="/feedback/:token" element={<FeedbackCandidaturaPage />} />
              <Route path="/motivo/:token" element={<MotivoPage />} />
              <Route path="/autorizar/:token" element={<AutorizarPage />} />
              <Route path="/indicadores" element={<IndicadoresPage />} />
              <Route path="/curriculo" element={<CurriculoPage />} />
              <Route path="/vagas" element={<VagasPage />} />
              <Route path="/vagas/nova" element={<NovaVagaPage />} />
              <Route path="/vagas/:id" element={<VagaPage />} />
              <Route path="/oportunidades" element={<OportunidadesPage />} />
              <Route path="/empresas" element={<CompaniesPage />} />
              <Route path="/como-funciona" element={<HowItWorksPage />} />
              <Route path="/para-empresas" element={<ParaEmpresasPage />} />
              <Route path="/painel" element={<PainelPage />} />
              <Route path="/conta" element={<ContaPage />} />
              <Route path="/minhas-reclamacoes" element={<MinhasReclamacoesPage />} />
              <Route path="/por-que" element={<PorQuePage />} />
              <Route path="/perguntas-frequentes" element={<PerguntasPage />} />
              <Route path="/cobrar-retorno" element={<CobrarRetornoPage />} />
              <Route path="/guia/rh-como-dar-retorno-ao-candidato" element={<GuiaRhPage />} />
              <Route path="/empresas/:id" element={<CompanyPage />} />
              <Route path="/entrar" element={<LoginPage />} />
              <Route path="/esqueci-senha" element={<EsqueciSenhaPage />} />
              <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
              <Route path="/confirmar-email" element={<ConfirmarEmailPage />} />
              <Route path="/termos" element={<TermosPage />} />
              <Route path="/privacidade" element={<PrivacidadePage />} />
              <Route path="/selo" element={<SeloPage />} />
              {/* Protótipo escondido (sem link no menu, noindex) para mostrar a ideia a escolas e professores */}
              <Route path="/parceiros/demo" element={<ParceirosDemoPage />} />
              <Route path="/parceiro" element={<ParceiroPainelPage />} />
              <Route path="/capacitacao" element={<CapacitacaoPage />} />
              <Route path="/aula" element={<ParceirosDemoPage />} />
              <Route path="/guia/como-saber-se-uma-empresa-da-retorno" element={<GuiaRetornoPage />} />
              <Route path="/admin" element={<Suspense fallback={<div className="p-8 text-gray-400">Carregando painel...</div>}><AdminPage /></Suspense>} />
              <Route path="/buscar-candidatos" element={<Navigate to="/vagas?buscar=1" replace />} />
              {/* Endereço legível da vaga: /gateware/analista-de-negocios-senior. Fica no FIM, logo antes do
                  404, porque um caminho de duas partes só é vaga depois que nenhuma rota nomeada o reivindicou —
                  ordem invertida faria esta rota engolir /empresas/:id, /relato/:id e todas as futuras. */}
              <Route path="/:empresaSlug/:vagaSlug" element={<VagaPage />} />
              {/* URL COM PONTUACAO GRUDADA -> limpa e reencaminha, em vez de mostrar "nao encontrada".
                  Link colado em post/DM quase sempre gruda o ponto final da frase ("acesse eairh.com.br/vagas.")
                  e alguns clientes levam junto virgula, parenteses ou aspas. Em 28/08/2026 o log registrou uma
                  visita a /vagas. — pessoa que veio de uma divulgacao NOSSA e bateu em tela de erro.
                  O servidor responde 200 (e o SPA), entao so o router pode consertar. */}
              <Route path="*" element={<UrlSuja />} />
            </Routes>
          </div>
          <MolduraDoSite />

        </div>
        </ComplaintModalProvider>
      </BrowserRouter>
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
